/**
 * Time Utilities
 *
 * Convention: Fields ending in `Sec` are Unix seconds, `Ms` are JS milliseconds.
 * - On-chain data: Unix seconds
 * - JavaScript: Milliseconds (Date.now())
 */

export function secToMs(seconds: number): number {
	return seconds * 1000
}

export function msToSec(ms: number): number {
	return Math.floor(ms / 1000)
}

export function nowSec(): number {
	return msToSec(Date.now())
}

export function nowMs(): number {
	return Date.now()
}

export function isPastSec(seconds: number): boolean {
	return nowSec() >= seconds
}

export function isPastMs(ms: number): boolean {
	return nowMs() >= ms
}

export function formatDateSec(seconds: number): string {
	return new Date(secToMs(seconds)).toLocaleDateString()
}

export function formatDateTimeSec(seconds: number): string {
	return new Date(secToMs(seconds)).toLocaleString()
}

export function formatDateMs(ms: number): string {
	return new Date(ms).toLocaleDateString()
}

export function formatDateTimeMs(ms: number): string {
	return new Date(ms).toLocaleString()
}

export function deadlineSec(secondsFromNow: number): number {
	return nowSec() + secondsFromNow
}

export function deadlineMs(secondsFromNow: number): number {
	return nowMs() + secondsFromNow * 1000
}

/**
 * Format a timestamp as relative time (e.g., "5m ago", "2h ago", "3d ago")
 * Similar to date-fns formatDistanceToNow but simpler
 */
export function formatTimeAgoSec(timestampSec: number): string {
	const nowSeconds = nowSec()
	const diffSec = nowSeconds - timestampSec

	if (diffSec < 60) {
		return "just now"
	}

	const diffMin = Math.floor(diffSec / 60)
	if (diffMin < 60) {
		return `${diffMin}m ago`
	}

	const diffHour = Math.floor(diffMin / 60)
	if (diffHour < 24) {
		return `${diffHour}h ago`
	}

	const diffDay = Math.floor(diffHour / 24)
	if (diffDay < 30) {
		return `${diffDay}d ago`
	}

	const diffMonth = Math.floor(diffDay / 30)
	if (diffMonth < 12) {
		return `${diffMonth}mo ago`
	}

	const diffYear = Math.floor(diffMonth / 12)
	return `${diffYear}y ago`
}

export function formatTimeAgoMs(timestampMs: number): string {
	return formatTimeAgoSec(msToSec(timestampMs))
}
